import { useState, useEffect } from 'react'
import { Button } from '../../../shared/components/ui/Button.js'
import { Input } from '../../../shared/components/ui/Input.js'
import { Modal } from '../../../shared/components/ui/Modal.js'
import { api } from './api.js'
import type { Conta } from './types.js'

interface Props {
  open: boolean
  onClose: () => void
  conta: Conta
}

export function ResetSenhaModal({ open, onClose, conta }: Props) {
  const [loading, setLoading] = useState(false)
  const [tempPassword, setTempPassword] = useState<string | null>(null)
  const [customPassword, setCustomPassword] = useState('')
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (open) {
      setTempPassword(null)
      setCustomPassword('')
      setError('')
      setCopied(false)
    }
  }, [open])

  async function handleConfirm() {
    if (customPassword && customPassword.length < 6) {
      setError('A senha deve ter no mínimo 6 caracteres')
      return
    }
    setLoading(true)
    setError('')
    try {
      const body = customPassword ? { password: customPassword } : {}
      const res = await api.post<{ tempPassword: string }>(
        `/api/v1/super/contas/${conta.id}/reset-password`,
        body,
      )
      setTempPassword(res.tempPassword)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro inesperado')
    } finally {
      setLoading(false)
    }
  }

  function handleCopy() {
    if (tempPassword) {
      navigator.clipboard.writeText(tempPassword)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Redefinir senha">
      {tempPassword ? (
        <div className="flex flex-col gap-4 py-2">
          <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <p className="text-sm text-emerald-800">
              Senha redefinida com sucesso para <strong>{conta.email}</strong>.
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#64748b] mb-1.5">
              Nova senha temporária
            </label>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-[#f8fafc] border border-[#e2e8f0] rounded-lg px-3 py-2 text-sm font-mono text-[#0f172a] select-all">
                {tempPassword}
              </code>
              <button
                onClick={handleCopy}
                className="shrink-0 px-3 py-2 rounded-lg border border-[#e2e8f0] text-sm text-[#64748b] hover:bg-[#f1f5f9] transition-colors"
              >
                {copied ? 'Copiado!' : 'Copiar'}
              </button>
            </div>
          </div>

          <p className="text-xs text-[#94a3b8]">
            Envie essa senha ao cliente. Ele poderá alterá-la após o primeiro login.
          </p>

          <Button onClick={onClose} className="w-full mt-1">
            Fechar
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" className="shrink-0 mt-0.5">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            <p className="text-sm text-amber-800">
              A senha de <strong>{conta.email}</strong> será substituída.
            </p>
          </div>

          <Input
            label="Nova senha"
            type="password"
            placeholder="Deixe vazio para gerar automaticamente"
            value={customPassword}
            onChange={(e) => setCustomPassword(e.target.value)}
          />

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1" disabled={loading}>
              Cancelar
            </Button>
            <Button onClick={handleConfirm} loading={loading} className="flex-1">
              {customPassword ? 'Redefinir senha' : 'Gerar nova senha'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}
